import {
  Button,
  Checkbox,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea
} from '@invent/wl-ui-kit-next/ui';
import { ChevronDown, ChevronRight } from 'lucide-react';
import * as React from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  NotPermitted,
  StatusChip
} from '@/design';
import { ApiError } from '@/domains/api';
import { useCan } from '@/domains/auth';
import { ddApi } from '../../api/dd-api';
import type {
  DdMeetingKind,
  DdSectionKind,
  IDdNotesPreview,
  IDdSection,
  IDdTemplate,
  IDdTemplateBody
} from '../../api/dd-contracts';
import { AdminPage, LoadError, Section, TableSkeleton, useAdminData } from '../../components/admin-page';
import { DD_MEETING_KINDS } from '../../dd';
import { refusalMessage } from '../../refusal';
import { DdSurveySelect, surveyParam } from './dd-parts';

/**
 * Setup → DD templates (SR-324; survey-api's due-diligence design D4): a survey's template of one
 * meeting kind — the DD call, a follow-up call, an in-person round (SR-449 / SR-501, `?kind=`) —
 * version by version, each kind versioned on its own. A version is frozen: *Publish as a new version* writes the next
 * one and retires the last, and an interview keeps the version it was opened on. A section's
 * *headings* are the note-taker's headings that fill it besides its label (REC-9.5's mapping).
 * Reading is `internal_fields.read`; publishing is `internal_fields.author`, which survey-api
 * checks, along with every rule a version must meet.
 *
 * **A draft before a version** (SR-525, F3): *Save draft* keeps the editor's sections on survey-api
 * as the kind's one draft, unpublished, which the editor opens on next time; *Discard draft* drops
 * it; a publish takes it. Each version says how many interviews were opened on it.
 *
 * **A section's code follows its label** (F1): a new section's code is made from its label until
 * someone types one under *Advanced*, where the codes are drawn — a stored section keeps the code
 * its answers are filed under. survey-api still judges every code, and a refusal naming one opens
 * *Advanced*. **A Granola sample is tried before publishing** (F2): the editor's sections as they
 * stand, unsaved, split by survey-api's own splitter (`POST /dd-templates/previews`), nothing
 * written.
 */

const KINDS: ReadonlyArray<{ value: DdSectionKind; label: string }> = [
  { value: 'long_text', label: 'Long text' },
  { value: 'text', label: 'One line' },
  { value: 'bool', label: 'Yes / no' }
];

type EditedSection = Omit<IDdSection, 'headings'> & {
  headings: string;
  /** The code is made from the label: a new section's, until a code is typed under *Advanced*. */
  derived: boolean;
};

function editable(template: IDdTemplate): EditedSection[] {
  return template.sections.map((section) => ({ ...section, headings: section.headings.join(', '), derived: false }));
}

/** The body survey-api takes for a draft, a publish or a preview: the editor's sections trimmed. */
function bodyOf(survey: string, kind: DdMeetingKind, sections: readonly EditedSection[]): IDdTemplateBody {
  return {
    survey,
    kind,
    sections: sections.map((section) => ({
      code: section.code.trim(),
      label: section.label.trim(),
      kind: section.kind,
      required: section.required,
      headings: section.headings
        .split(',')
        .map((heading) => heading.trim())
        .filter(Boolean)
    }))
  };
}

/** A code made from a label, in survey-api's shape (a letter, then letters, digits or `_`, at most
 *  63) and apart from `taken` — a proposal: survey-api judges it like a typed one. */
export function codeFromLabel(label: string, taken: readonly string[]): string {
  const words = label
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  const base = (/^[a-z]/.test(words) ? words : `section_${words}`).slice(0, 58).replace(/_+$/, '');
  let code = base;
  for (let n = 2; taken.includes(code); n++) code = `${base}_${n}`;
  return code;
}

/** Whether a refusal names a section's code — *Advanced* is opened to show it. */
function namesCode(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    (error.problem?.problems ?? []).some((problem) => /^sections\[\d+\]\.code$/.test(problem.target))
  );
}

/** The address's `?kind=`, a kind this page knows, else the DD call. */
function kindParam(params: URLSearchParams): DdMeetingKind {
  const asked = params.get('kind');
  return DD_MEETING_KINDS.find((entry) => entry.value === asked)?.value ?? 'dd_call';
}

export function DdTemplatesPage() {
  const [params, setParams] = useSearchParams();
  const survey = surveyParam(params);
  const kind = kindParam(params);
  const choose = (next: { survey?: string; kind?: DdMeetingKind }) => {
    const query = new URLSearchParams({ survey: next.survey ?? survey });
    const chosen = next.kind ?? kind;
    if (chosen !== 'dd_call') query.set('kind', chosen);
    setParams(query, { replace: true });
    setSaid(null);
  };
  const can = useCan();
  const author = can('internal_fields.author');
  // Tagged with the survey and kind they are of: while another's load, the last ones stay on
  // screen, and an editor seeded from them must not take the new survey's or kind's name.
  const asked = `${survey}|${kind}`;
  const versions = useAdminData(
    async (signal) => ({ asked, templates: await ddApi.templates(survey, kind, signal) }),
    [asked]
  );
  const loaded = versions.data?.asked === asked ? versions.data.templates : null;
  const current = loaded?.find((template) => template.status === 'published') ?? null;
  const draft = loaded?.find((template) => template.status === 'draft') ?? null;
  // The editor opens on the draft where there is one, else on the version in use.
  const seed = draft ?? current;
  const [said, setSaid] = React.useState<Nullable<string>>(null);

  return (
    <AdminPage title="DD templates">
      <div className="grid gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <DdSurveySelect value={survey} onChange={(value) => choose({ survey: value })} />
          <Select value={kind} onValueChange={(value) => choose({ kind: value as DdMeetingKind })}>
            <SelectTrigger className="h-8 w-72" aria-label="Meeting">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DD_MEETING_KINDS.map((entry) => (
                <SelectItem key={entry.value} value={entry.value}>
                  {entry.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {versions.error && <LoadError message={versions.error} />}
        {!loaded && !versions.error && <TableSkeleton rows={4} />}
        {said && (
          <p className="text-sm" role="status">
            {said}
          </p>
        )}
        {seed && (
          <TemplateEditor
            key={`${asked}:${seed.status}:${seed.templateId}:${seed.version}`}
            survey={survey}
            kind={kind}
            current={seed}
            author={author}
            onSaved={async (message) => {
              setSaid(message);
              await versions.reload();
            }}
          />
        )}
        {loaded && loaded.length > 0 && (
          <Section title="Versions">
            <ul className="grid gap-1 text-[0.8125rem]" aria-label="Versions">
              {loaded.map((template) => (
                <li key={template.templateId} className="flex items-center gap-2" data-dd-template={template.status}>
                  <span className="tabular-nums">{template.status === 'draft' ? 'Draft' : `v${template.version}`}</span>
                  <StatusChip
                    tone={template.status === 'published' ? 'success' : template.status === 'draft' ? 'attention' : 'neutral'}
                  >
                    {template.status === 'draft'
                      ? 'not published'
                      : template.version === 0
                        ? 'Default, not yet planted'
                        : template.status}
                  </StatusChip>
                  {template.version > 0 && template.interviews !== undefined && (
                    <span className="whitespace-nowrap tabular-nums">{interviewsOn(template)}</span>
                  )}
                  <span className="text-muted-foreground">
                    {template.sections.map((section) => section.label).join(' · ')}
                  </span>
                </li>
              ))}
            </ul>
          </Section>
        )}
      </div>
    </AdminPage>
  );
}

/** "12 interviews on v3" — a version's use (F3). */
function interviewsOn(template: IDdTemplate): string {
  const n = template.interviews ?? 0;
  return `${n.toLocaleString('en-US')} ${n === 1 ? 'interview' : 'interviews'} on v${template.version}`;
}

function TemplateEditor({
  survey,
  kind,
  current,
  author,
  onSaved
}: {
  survey: string;
  kind: DdMeetingKind;
  /** The kind's draft, else the version in use. */
  current: IDdTemplate;
  author: boolean;
  /** The page says it and reads the versions again: the editor is remounted on what it saved. */
  onSaved: (message: string) => Promise<void>;
}) {
  const [sections, setSections] = React.useState<EditedSection[]>(() => editable(current));
  const [busy, setBusy] = React.useState(false);
  const [said, setSaid] = React.useState<Nullable<string>>(null);
  const [advanced, setAdvanced] = React.useState(false);
  const [discarding, setDiscarding] = React.useState(false);
  const notPermitted = 'dd-templates-not-permitted';
  const isDraft = current.status === 'draft';

  const change = (index: number, patch: Partial<EditedSection>) =>
    setSections((all) =>
      all.map((section, at) => {
        if (at !== index) return section;
        const next = { ...section, ...patch };
        // A label typed moves a derived code with it; a code typed stops following the label.
        if (patch.code !== undefined) return { ...next, derived: false };
        if (patch.label !== undefined && section.derived) {
          const taken = all.filter((_, other) => other !== index).map((one) => one.code);
          return { ...next, code: codeFromLabel(patch.label, taken) };
        }
        return next;
      })
    );

  /** One write, said by the page on success and here on a refusal. The presses stay off until the
   *  versions are read again: a second *Publish* on the sections still on screen would put out a
   *  second, identical version. */
  const write = async (run: () => Promise<string>, fallback: string) => {
    setBusy(true);
    setSaid(null);
    try {
      await onSaved(await run());
    } catch (error) {
      if (namesCode(error)) setAdvanced(true);
      setSaid(refusalMessage(error, fallback));
    } finally {
      setBusy(false);
    }
  };
  const publish = () =>
    write(async () => {
      const published = await ddApi.publishTemplate(bodyOf(survey, kind, sections));
      return `Version ${published.version} is published; new ${kind === 'dd_call' ? 'interviews' : 'meetings of this kind'} open on it.`;
    }, 'The version could not be published.');
  const saveDraft = () =>
    write(async () => {
      await ddApi.saveTemplateDraft(bodyOf(survey, kind, sections));
      return 'The draft is saved. Nothing changes for new interviews until it is published.';
    }, 'The draft could not be saved.');
  const discardDraft = () => {
    setDiscarding(false);
    return write(async () => {
      await ddApi.discardTemplateDraft(survey, kind);
      return 'The draft is discarded.';
    }, 'The draft could not be discarded.');
  };

  return (
    <Section
      title={
        isDraft
          ? 'Draft, not published'
          : `${current.version === 0 ? 'The default template' : `Version ${current.version}, in use`}${
              current.isDefault ? ' · stand-in outline' : ''
            }${current.version > 0 && current.interviews !== undefined ? ` · ${interviewsOn(current)}` : ''}`
      }
      description="Each section is one field of the interview; its headings are the note-taker's headings that fill it."
    >
      {!author && <NotPermitted id={notPermitted} what="publishing templates" />}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="mb-2 gap-1 px-1"
        aria-expanded={advanced}
        onClick={() => setAdvanced(!advanced)}
      >
        {advanced ? <ChevronDown className="size-4" aria-hidden /> : <ChevronRight className="size-4" aria-hidden />}
        Advanced
      </Button>
      <Table aria-label="Sections">
        <TableHeader>
          <TableRow>
            {advanced && <TableHead>Code</TableHead>}
            <TableHead>Label</TableHead>
            <TableHead>Kind</TableHead>
            <TableHead>Required</TableHead>
            <TableHead>Headings</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {sections.map((section, index) => (
            <TableRow key={index} data-dd-section-row={index}>
              {advanced && (
                <TableCell>
                  <Input
                    className="h-8 w-40"
                    aria-label={`Section ${index + 1} code`}
                    value={section.code}
                    readOnly={!author}
                    onChange={(event) => change(index, { code: event.target.value })}
                  />
                </TableCell>
              )}
              <TableCell>
                <Input
                  className="h-8"
                  aria-label={`Section ${index + 1} label`}
                  value={section.label}
                  readOnly={!author}
                  onChange={(event) => change(index, { label: event.target.value })}
                />
              </TableCell>
              <TableCell>
                <Select
                  value={section.kind}
                  onValueChange={(value) => change(index, { kind: value as DdSectionKind })}
                  disabled={!author}
                >
                  <SelectTrigger className="h-8 w-32" aria-label={`Section ${index + 1} kind`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {KINDS.map((kind) => (
                      <SelectItem key={kind.value} value={kind.value}>
                        {kind.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </TableCell>
              <TableCell>
                <Checkbox
                  aria-label={`Section ${index + 1} required`}
                  checked={section.required}
                  disabled={!author}
                  onCheckedChange={(checked) => change(index, { required: checked === true })}
                />
              </TableCell>
              <TableCell>
                <Input
                  className="h-8"
                  aria-label={`Section ${index + 1} headings`}
                  placeholder="Comma-separated"
                  value={section.headings}
                  readOnly={!author}
                  onChange={(event) => change(index, { headings: event.target.value })}
                />
              </TableCell>
              <TableCell>
                {author && (
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    onClick={() => setSections((all) => all.filter((_, at) => at !== index))}
                  >
                    Remove
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {author && (
        <div className="mt-3 flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() =>
              setSections((all) => [
                ...all,
                { code: '', label: '', kind: 'long_text', required: false, headings: '', derived: true }
              ])
            }
          >
            Add a section
          </Button>
          <SampleTest survey={survey} kind={kind} sections={sections} />
          <span className="flex-1" />
          <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => void saveDraft()}>
            Save draft
          </Button>
          {isDraft && (
            <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => setDiscarding(true)}>
              Discard draft
            </Button>
          )}
          <Button type="button" size="sm" disabled={busy} onClick={() => void publish()}>
            {busy ? 'Saving…' : 'Publish as a new version'}
          </Button>
        </div>
      )}
      <Dialog open={discarding} onOpenChange={setDiscarding}>
        <DialogContent data-dd-discard-draft="">
          <DialogHeader>
            <DialogTitle>Discard the draft?</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p className="text-sm">The editor goes back to the version in use.</p>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" disabled={busy} onClick={() => setDiscarding(false)}>
              Keep it
            </Button>
            <Button type="button" disabled={busy} onClick={() => void discardDraft()}>
              Discard draft
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {said && (
        <p className="mt-2 text-sm" role="alert">
          {said}
        </p>
      )}
    </Section>
  );
}

/**
 * *Test with a Granola sample* (F2): a sample pasted, split against the editor's sections as they
 * stand — unsaved — by survey-api's splitter, each section's share shown, nothing written. A
 * section change after a split clears it, so a result is always of the sections on screen.
 */
function SampleTest({
  survey,
  kind,
  sections
}: {
  survey: string;
  kind: DdMeetingKind;
  sections: readonly EditedSection[];
}) {
  const [open, setOpen] = React.useState(false);
  const [text, setText] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [said, setSaid] = React.useState<Nullable<string>>(null);
  const body = bodyOf(survey, kind, sections);
  // The sample is part of what was asked: a split answered after the text moved is not shown.
  const asked = JSON.stringify([body, text]);
  const [preview, setPreview] = React.useState<Nullable<{ asked: string; answer: IDdNotesPreview }>>(null);
  const shown = preview?.asked === asked ? preview.answer : null;
  const labelOf = (code: string) => body.sections.find((section) => section.code === code)?.label || code;

  const split = async () => {
    setBusy(true);
    setSaid(null);
    try {
      setPreview({ asked, answer: await ddApi.previewTemplate({ ...body, text }) });
    } catch (error) {
      setPreview(null);
      setSaid(refusalMessage(error, 'The sample could not be split.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button type="button" variant="outline" size="sm" onClick={() => setOpen(true)}>
        Test with a Granola sample
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl" data-dd-sample="">
          <DialogHeader>
            <DialogTitle>Test with a Granola sample</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Textarea
              rows={8}
              className="max-h-72"
              aria-label="Granola sample"
              value={text}
              onChange={(event) => {
                setText(event.target.value);
                setPreview(null);
              }}
            />
            {said && (
              <p role="alert" className="text-sm">
                {said}
              </p>
            )}
            {shown && (
              <dl aria-label="Split by section" className="grid max-h-80 gap-2 overflow-y-auto text-[0.8125rem]">
                {body.sections.map((section) => {
                  const share = shown.sections[section.code];
                  return (
                    <div key={section.code} data-dd-sample-section={section.code} className="grid gap-0.5">
                      <dt className="font-medium">{section.label || section.code}</dt>
                      <dd className={share ? 'whitespace-pre-wrap' : 'text-muted-foreground'}>
                        {share || 'Nothing from the sample'}
                      </dd>
                    </div>
                  );
                })}
                {shown.unmatched && (
                  <div className="grid gap-0.5" data-dd-sample-unmatched="">
                    <dt className="font-medium">Kept apart, no section took it</dt>
                    <dd className="whitespace-pre-wrap text-muted-foreground">{shown.unmatched}</dd>
                  </div>
                )}
                {shown.missing.length > 0 && (
                  <div className="grid gap-0.5" data-dd-sample-missing="">
                    <dt className="font-medium">No heading for</dt>
                    <dd className="text-muted-foreground">{shown.missing.map(labelOf).join(', ')}</dd>
                  </div>
                )}
              </dl>
            )}
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Close
            </Button>
            <Button type="button" disabled={busy || text.trim() === ''} onClick={() => void split()}>
              {busy ? 'Splitting…' : 'Split'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
